import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  LinearProgress,
  Portal,
  Slide,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { useMutationQueue } from '../../providers/MutationQueueProvider';

export default function OfflineBanner({ flushing }) {
  const theme = useTheme();
  const online = useOnlineStatus();
  const { queueIsFlushing, percent, progress } = useMutationQueue();

  const [variant, setVariant] = useState(null);
  const [open, setOpen] = useState(false);
  const prevVariantRef = useRef(null);

  useEffect(() => {
    const nextVariant = !online
      ? 'offline'
      : flushing || queueIsFlushing
      ? 'syncing'
      : null;

    if (nextVariant !== prevVariantRef.current) {
      prevVariantRef.current = nextVariant;
      setVariant(nextVariant);
      setOpen(!!nextVariant);
    }
  }, [flushing, online, queueIsFlushing]);

  if (!variant) return null;

  const content =
    variant === 'offline'
      ? {
          severity: 'warning',
          message: (
            <>
              You’re offline. Your changes will be saved on this device and
              submitted automatically when you are reconnected! 🚀
            </>
          ),
        }
      : {
          severity: 'success',
          message: (
            <>
              Syncing data… Please don’t close this window.
              {queueIsFlushing && (
                <div style={{ marginTop: 8 }}>
                  <LinearProgress
                    variant={percent == null ? 'indeterminate' : 'determinate'}
                    value={percent == null ? undefined : percent}
                  />
                  {percent != null && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {progress.processed}/{progress.total} ({percent}%)
                    </div>
                  )}
                </div>
              )}
            </>
          ),
        };

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.snackbar + 1,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Slide in={open} direction="down" mountOnEnter unmountOnExit>
          <Box sx={{ width: '100%', pointerEvents: 'auto' }}>
            <Alert
              variant="filled"
              severity={content.severity}
              sx={{ borderRadius: 0 }}
              action={
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setOpen(false)}
                  aria-label="close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {content.message}
            </Alert>
          </Box>
        </Slide>
      </Box>
    </Portal>
  );
}
